"use client";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { logger } from "@/lib/logger";

// Clear basket storage after checkout completion BEFORE Zustand hydration
// This runs when the module is first imported, before the store is created
if (typeof window !== "undefined") {
  try {
    // Check if user just completed checkout (flag set when clicking "Proceed to Checkout")
    const checkoutInProgress = sessionStorage.getItem("checkout-in-progress");

    if (checkoutInProgress === "true") {
      logger.info("Detected checkout completion - clearing basket");

      // Clear the basket from localStorage
      const existingData = localStorage.getItem("basket-storage");
      if (existingData) {
        localStorage.removeItem("basket-storage");
        logger.info("Basket storage cleared successfully");
      }

      // Remove the flag
      sessionStorage.removeItem("checkout-in-progress");
    }
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    logger.error("Error clearing basket storage", err);
  }
}

// Enhanced product image interface for basket items
type BasketProductImage = {
  id: string;
  url: string;
  altText: string | null;
  width: number | null;
  height: number | null;
};

// Enhanced variant information for basket items
type BasketVariantInfo = {
  id: string;
  title: string;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
  price: {
    amount: string;
    currencyCode: string;
  };
  compareAtPrice?: {
    amount: string;
    currencyCode: string;
  } | null;
};

export type BasketItem = {
  id: string;
  name: string;
  price: number;
  quantity: number;
  // Enhanced product information
  images: BasketProductImage[];
  primaryImage?: BasketProductImage; // Quick access to main image
  brand?: string;
  category?: string;
  handle?: string; // For linking back to product page
  // Enhanced variant information
  variant?: BasketVariantInfo;
  // Legacy support
  image?: string; // Deprecated - kept for backward compatibility
};

export type BasketStore = {
  items: BasketItem[];
  count: number;
  total: number;
  isLoading: boolean;
  userId: string | null;
  lastSyncedAt: number | null;
  cartId: string | null; // Add cartId to the store

  // Actions
  addItem: (
    item: Omit<BasketItem, "quantity"> & { quantity?: number }
  ) => Promise<void>;
  removeItem: (id: string, variantId?: string) => Promise<void>;
  updateQuantity: (
    id: string,
    quantity: number,
    variantId?: string
  ) => Promise<void>;
  clearBasket: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  setCartId: (cartId: string | null) => void; // Action to set cartId

  // Authentication-aware actions
  syncWithUser: (userId: string, userBasketItems?: BasketItem[]) => void;
  clearUserData: () => void;
  mergeGuestBasket: (guestItems: BasketItem[]) => void;

  // Computed
  getItemQuantity: (id: string, variantId?: string) => number;
};

export const useBasketStore = create<BasketStore>()(
  persist(
    immer((set, get) => ({
      items: [],
      count: 0,
      total: 0,
      isLoading: false,
      userId: null,
      lastSyncedAt: null,
      cartId: null,

      setCartId: (cartId) =>
        set((state) => {
          state.cartId = cartId;
        }),

      addItem: async (item) => {
        set((state) => {
          // Create a unique identifier that includes variant information
          const itemKey = item.variant
            ? `${item.id}-${item.variant.id}`
            : item.id;

          const existingItem = state.items.find((i) => {
            const existingKey = i.variant ? `${i.id}-${i.variant.id}` : i.id;
            return existingKey === itemKey;
          });

          const quantity = item.quantity || 1;

          if (existingItem) {
            existingItem.quantity += quantity;
          } else {
            state.items.push({ ...item, quantity });
          }

          // Update computed values
          state.count = state.items.reduce(
            (sum, basketItem) => sum + basketItem.quantity,
            0
          );
          state.total = state.items.reduce(
            (sum, basketItem) => sum + basketItem.price * basketItem.quantity,
            0
          );
        });
      },

      removeItem: async (id, variantId) => {
        set((state) => {
          const itemKey = variantId ? `${id}-${variantId}` : id;
          state.items = state.items.filter((basketItem) => {
            const basketItemKey = basketItem.variant
              ? `${basketItem.id}-${basketItem.variant.id}`
              : basketItem.id;
            return basketItemKey !== itemKey;
          });
          state.count = state.items.reduce(
            (sum, basketItem) => sum + basketItem.quantity,
            0
          );
          state.total = state.items.reduce(
            (sum, basketItem) => sum + basketItem.price * basketItem.quantity,
            0
          );
        });
      },

      updateQuantity: async (id, quantity, variantId) => {
        set((state) => {
          const itemKey = variantId ? `${id}-${variantId}` : id;

          if (quantity <= 0) {
            state.items = state.items.filter((basketItem) => {
              const basketItemKey = basketItem.variant
                ? `${basketItem.id}-${basketItem.variant.id}`
                : basketItem.id;
              return basketItemKey !== itemKey;
            });
          } else {
            const targetItem = state.items.find((i) => {
              const basketItemKey = i.variant
                ? `${i.id}-${i.variant.id}`
                : i.id;
              return basketItemKey === itemKey;
            });
            if (targetItem) {
              targetItem.quantity = quantity;
            }
          }

          state.count = state.items.reduce(
            (sum, basketItem) => sum + basketItem.quantity,
            0
          );
          state.total = state.items.reduce(
            (sum, basketItem) => sum + basketItem.price * basketItem.quantity,
            0
          );
        });
      },

      clearBasket: async () => {
        set((state) => {
          state.items = [];
          state.count = 0;
          state.total = 0;
          state.cartId = null;
        });
      },

      setLoading: (loading) =>
        set((state) => {
          state.isLoading = loading;
        }),

      // Authentication-aware methods
      syncWithUser: (userId, userBasketItems = []) =>
        set((state) => {
          const currentItems = [...state.items];
          state.userId = userId;
          state.lastSyncedAt = Date.now();

          // If user has saved basket items, merge with current basket
          if (userBasketItems.length > 0) {
            const mergedItems = [...userBasketItems];

            // Add any items from current basket that aren't in user's saved basket
            for (const currentItem of currentItems) {
              const existingItem = mergedItems.find(
                (item) => item.id === currentItem.id
              );
              if (existingItem) {
                // Keep the higher quantity
                existingItem.quantity = Math.max(
                  existingItem.quantity,
                  currentItem.quantity
                );
              } else {
                mergedItems.push(currentItem);
              }
            }

            state.items = mergedItems;
          } else {
            // User has no saved basket, keep current items
            state.items = currentItems;
          }

          // Recalculate totals
          state.count = state.items.reduce(
            (sum, item) => sum + item.quantity,
            0
          );
          state.total = state.items.reduce(
            (sum, item) => sum + item.price * item.quantity,
            0
          );
        }),

      clearUserData: () =>
        set((state) => {
          state.userId = null;
          state.lastSyncedAt = null;
          // Optionally clear basket items on logout (configurable)
          // state.items = [];
          // state.count = 0;
          // state.total = 0;
        }),

      mergeGuestBasket: (guestItems) =>
        set((state) => {
          const currentItems = [...state.items];

          // Merge guest items with current items
          for (const guestItem of guestItems) {
            const existingItem = currentItems.find(
              (item) => item.id === guestItem.id
            );
            if (existingItem) {
              existingItem.quantity += guestItem.quantity;
            } else {
              currentItems.push(guestItem);
            }
          }

          state.items = currentItems;
          state.count = state.items.reduce(
            (sum, item) => sum + item.quantity,
            0
          );
          state.total = state.items.reduce(
            (sum, item) => sum + item.price * item.quantity,
            0
          );
        }),

      getItemQuantity: (id, variantId?: string) => {
        const itemKey = variantId ? `${id}-${variantId}` : id;
        const targetItem = get().items.find((basketItem) => {
          const basketItemKey = basketItem.variant
            ? `${basketItem.id}-${basketItem.variant.id}`
            : basketItem.id;
          return basketItemKey === itemKey;
        });
        return targetItem?.quantity || 0;
      },
    })),
    {
      name: "basket-storage",
      partialize: (state) => ({
        items: state.items,
        count: state.count,
        total: state.total,
        userId: state.userId,
        lastSyncedAt: state.lastSyncedAt,
        cartId: state.cartId,
      }),
    }
  )
);

/**
 * Optimistic basket actions hook
 * Provides direct access to basket store methods for optimistic UI updates
 * Cart synchronization with Shopify is handled separately by useCartSync
 */
export const useOptimisticBasketActions = () => {
  const store = useBasketStore();

  return {
    addItem: (item: Omit<BasketItem, "quantity"> & { quantity?: number }) => {
      return store.addItem(item);
    },

    removeItem: (id: string, variantId?: string) => {
      return store.removeItem(id, variantId);
    },

    updateQuantity: (id: string, quantity: number, variantId?: string) => {
      return store.updateQuantity(id, quantity, variantId);
    },

    clearBasket: () => {
      return store.clearBasket();
    },
  };
};
